// PHASE-2.03 START
import React from 'react';
import type { HistoryItem } from './types';
import { formatTimeAgo } from '@/lib/utils';
import { cn } from '@/lib/utils';
import { BookmarkIcon, TrashIcon } from '@heroicons/react/24/outline';
import { Button } from '@/components/ui/Button';

export function HistoryList({ items, onSelect, selectedId, pinnedIds = [], onPinToggle, onDelete }:{ items:HistoryItem[]; onSelect?:(id:string)=>void; selectedId?:string; pinnedIds?: string[]; onPinToggle?:(id:string)=>void; onDelete?:(id:string)=>void }){
  return (
    <div className="space-y-1" role="list">
      {items.map(it=> (
        <div key={it.id} className="relative group" role="listitem">
          <div
            role="button"
            tabIndex={0}
            aria-pressed={selectedId === it.id}
            onClick={()=> onSelect?.(it.id)}
            onKeyDown={(e)=>{ if(e.key==='Enter' || e.key===' ') { e.preventDefault(); onSelect?.(it.id); } }}
            className={cn(
              'sidebar-history-item',
              'w-full flex items-center gap-3 px-3 py-3 pr-14',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/20',
              'min-h-[56px] cursor-pointer relative',
              selectedId === it.id && 'active'
            )}
          >
            <div className="min-w-0 flex-1 text-left">
              <div className="text-sm font-medium truncate flex items-center gap-2">
                {pinnedIds.includes(it.id) && <BookmarkIcon aria-label="Pinned" className="h-3 w-3 text-amber-500 shrink-0" />}
                <span className="truncate">{it.title}</span>
              </div>
              <div className="text-xs text-muted-foreground/80 line-clamp-1 leading-relaxed mt-1">{it.excerpt}</div>
              <div className="mt-1 flex items-center gap-2 text-xs text-muted-foreground/60 min-w-0">
                {it.client && (
                  <span className="inline-flex items-center px-2 py-0.5 rounded bg-primary/10 text-primary text-xs font-medium truncate min-w-0 flex-shrink" title={it.client}>
                    {it.client}
                  </span>
                )}
                <span className="shrink-0 whitespace-nowrap">{formatTimeAgo(new Date(it.ts))}</span>
              </div>
            </div>
            {it.unread && <span aria-label="unread" className="ml-2 inline-block w-2 h-2 rounded-full bg-primary" />}
            {/* Hover-reveal action buttons */}
            <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
              {onPinToggle && (
                <Button
                  onClick={(e)=>{ e.stopPropagation(); onPinToggle(it.id); }}
                  title={pinnedIds.includes(it.id) ? 'Unpin' : 'Pin'}
                  aria-label={pinnedIds.includes(it.id) ? 'Unpin conversation' : 'Pin conversation'}
                  variant="ghost"
                  size="icon"
                  className="border border-border/40 bg-background/80 backdrop-blur-sm"
                >
                  <BookmarkIcon className={cn('h-3.5 w-3.5', pinnedIds.includes(it.id) ? 'text-amber-500' : 'text-muted-foreground')} />
                </Button>
              )}
              {onDelete && (
                <Button
                  onClick={(e)=>{ e.stopPropagation(); onDelete(it.id); }}
                  title="Delete"
                  aria-label="Delete conversation"
                  variant="ghost"
                  size="icon"
                  className="border border-border/40 bg-background/80 backdrop-blur-sm hover:bg-red-500/10 text-destructive"
                >
                  <TrashIcon className="h-3.5 w-3.5" />
                </Button>
              )}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}
// PHASE-2.03 END
